/**
 * @file GenericContractPricing.js
 * @version 1.9.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-09
 * @modified 2026-10-29
 * @description Reusable single-product contract-pricing tab. Drives the
 *              Minerals, Bags and Other contract-pricing tabs off the shared
 *              pricing.customer_contracts model + ContractPricingModal, keyed by
 *              a category id. Mirrors CompoundPricing's single-row layout minus
 *              the multi-product group editor (these categories are single
 *              product per contract). Parameterised by props so one component
 *              serves every simple category rather than cloning per tab.
 */

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API_URL } from '../config';
import { setContractsFulfilled, fulfilledAction } from '../utils/contractFulfilment';
import { isPastPeriodName, periodSortKey } from '../utils/periodHelper';
import { contractCoversToday } from '../utils/pricingHelpers';
import { makeContractLive, makeLiveAction } from '../utils/contractDraft';
import { formatQuantity } from '../utils/numberFormatters';
import { formatPrice } from '../utils/currencyFormatter';
import './SearchLayout.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import Dropdown from './Dropdown';
import Input from './Input';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import ContractPricingModal from './ContractPricingModal';
import ContactModal from './ContactModal';
import { ContractCustomerCell, ProductCell, PriceMarginCell, ContractGroupBadge } from './PricingBadges';
import RowActionsMenu from './RowActionsMenu';
import { openContractPdf } from '../utils/contractPdf';
import TableContextMenu from './TableContextMenu';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import RecalculateQuantitiesButton from './RecalculateQuantitiesButton';
import RefreshAutoLinksButton from './RefreshAutoLinksButton';
import ContractOrdersModal from './ContractOrdersModal';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { matchesAllTerms, searchFieldsSummary } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';

// Which columns the free-text search box looks at. Users can narrow this via
// the field-selector dropdown beside the search input.
const SEARCH_FIELDS = [
  { key: 'customer', label: 'Customer', get: c => c.company_name },
  { key: 'account', label: 'Account', get: c => c.account_no },
  // The product group a contract was made from (29 Oct 2026) is found as a product.
  { key: 'product', label: 'Product', get: c => [c.product_name, c.product_group && c.product_group.name].filter(Boolean).join(' ') },
  { key: 'code', label: 'Product Code', get: c => c.product_code },
  /*
   * Ours and the supplier's. Note the Contract column on THIS screen prints
   * only our own number, unlike blends and compounds which print both — so a
   * supplier reference is searchable here without being visible. That is
   * currently theoretical (no minerals, bags or other contract carries one),
   * and the CONTRACTED toggle already counts the field, so searching it keeps
   * the family of screens consistent. If these categories ever do carry
   * supplier references, the column should show them as blends does.
   */
  { key: 'contract', label: 'Contract #', get: c => [c.contract_number, c.supplier_contract_number].filter(Boolean).join(' ') },
];
const ALL_SEARCH_KEYS = SEARCH_FIELDS.map(f => f.key);
const SEARCH_FIELD_OPTIONS = SEARCH_FIELDS.map(f => ({ value: f.key, label: f.label }));

/**
 * @param {number} categoryId  pricing.product_categories id (4 minerals, 7 bags, 8 other)
 * @param {string} keyPrefix   namespace for persisted state + element ids (e.g. 'bagsPricing')
 * @param {string} title       lower-case noun used in the header blurb (e.g. 'bags')
 * @param {string} infoText    optional override for the header blurb
 */
/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  period: '', search: '', rep: '', mill: '',
  limit: '50', showArchived: false, showContracted: true
};
const FILTER_LABELS = { showArchived: 'Archived', showContracted: 'Contracted' };

function GenericContractPricing({ user, categoryId, keyPrefix, title, infoText }) {
  const [contracts, setContracts] = useState([]);
  const [periods, setPeriods] = useState([]);
  // "Add orders…" on a row: { set, title } (ContractOrdersModal, 28 Oct 2026).
  const [ordersFor, setOrdersFor] = useState(null);
  const [reps, setReps] = useState([]);
  const [mills, setMills] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedContactId, setSelectedContactId] = usePersistedState(`${keyPrefix}.contactModalId`, null);
  const [modalMode, setModalMode] = useState('edit');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [notesDialog, setNotesDialog] = useState({ isOpen: false, notes: '', title: '' });
  const [selectedIds, setSelectedIds] = useState([]);

  // Drag-to-resize columns (11: checkbox, customer, product, contract,
  // period, dates, supplier, quant, price/margin, rep, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns(keyPrefix, 11);

  useSlideOutButton([selectedIds.length]);

  const [filters, setFilters] = usePersistedState(`${keyPrefix}.filters`, {
    period: '',
    search: '',
    rep: '',
    mill: '',
    limit: '50',
    showArchived: false,
    showContracted: true
  });
  const [searchFields, setSearchFields] = usePersistedState(`${keyPrefix}.searchFields`, ALL_SEARCH_KEYS);
  const [sorting, setSorting] = usePersistedState(`${keyPrefix}.sorting`, { field: '', direction: 'asc' });
  const [pagination, setPagination] = usePersistedState(`${keyPrefix}.pagination`, {
    page: 1, limit: 50, total: 0, totalPages: 0
  });

  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const isCurrentPeriodName = (name) => {
    const p = periods.find(x => x.name === name);
    if (!p || !p.start_date || !p.end_date) return false;
    const start = (typeof p.start_date === 'string' ? p.start_date : new Date(p.start_date).toISOString()).slice(0, 10);
    const end = (typeof p.end_date === 'string' ? p.end_date : new Date(p.end_date).toISOString()).slice(0, 10);
    return todayStr >= start && todayStr <= end;
  };

  useEffect(() => {
    loadPeriods();
    loadReps();
    loadMills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  useEffect(() => {
    const delayTimer = setTimeout(() => { loadContracts(); }, 300);
    return () => clearTimeout(delayTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.period, filters.limit, categoryId]);

  const loadPeriods = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/periods?category_id=${categoryId}&is_active=all`, { credentials: 'include' });
      const data = await response.json();
      setPeriods(Array.isArray(data) ? data : (data.data || []));
    } catch (error) {
      console.error('Error loading periods:', error);
    }
  };

  const loadReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/reps?category_id=${categoryId}`, { credentials: 'include' });
      const data = await response.json();
      setReps(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading reps:', error);
    }
  };

  const loadMills = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pricing/distinct/suppliers?category_id=${categoryId}`, { credentials: 'include' });
      const suppliers = await response.json();
      setMills(Array.isArray(suppliers) ? suppliers : []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ category_id: categoryId, limit: 10000 });
      if (filters.period && filters.period !== '') params.append('period_id', filters.period);

      const response = await fetch(`${API_URL}/api/pricing/contracts?${params}`, { credentials: 'include' });
      const data = await response.json();

      const allContracts = data.data || [];
      setContracts(allContracts);

      const limit = parseInt(filters.limit) || 50;
      setPagination(prev => ({
        ...prev, page: 1, limit,
        total: allContracts.length,
        totalPages: Math.ceil(allContracts.length / limit)
      }));
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContracts([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  const handleSort = (field) => setSorting(prev => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const contractedIds = useMemo(() => {
    const set = new Set();
    for (const c of contracts) {
      const has = (c.contract_number && String(c.contract_number).trim())
               || (c.supplier_contract_number && String(c.supplier_contract_number).trim());
      if (has) set.add(c.id);
    }
    return set;
  }, [contracts]);

  const activeSearchFields = SEARCH_FIELDS.filter(f => searchFields.includes(f.key));

  const filteredAndSortedContracts = contracts
    .filter(contract => {
      if (filters.search) {
        const haystack = activeSearchFields.map(f => f.get(contract));
        if (!matchesAllTerms(haystack, filters.search)) return false;
      }
      if (filters.rep && contract.rep_name !== filters.rep) return false;
      if (filters.mill && contract.supplier_contact_id?.toString() !== filters.mill) return false;
      if (!filters.showArchived && contract.is_archived) return false;
      if (!filters.showContracted && contractedIds.has(contract.id)) return false;
      return true;
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;
      let aVal = a[sorting.field];
      let bVal = b[sorting.field];
      if (['quantity', 'cost', 'price', 'margin'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sorting.field === 'period_names') {
        // Periods sort by date, not by spelling — alphabetically "Summer 2026"
        // would come before "Winter 2025-26".
        aVal = periodSortKey(periods, aVal);
        bVal = periodSortKey(periods, bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  useEffect(() => {
    const limit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev, page: 1, limit,
      total: filteredAndSortedContracts.length,
      totalPages: Math.ceil(filteredAndSortedContracts.length / limit)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAndSortedContracts.length, filters.search, filters.rep, filters.mill, filters.limit, sorting.field, sorting.direction]);

  const paginatedContracts = filteredAndSortedContracts.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleAddNew = () => { setSelectedContract(null); setModalMode('edit'); setShowModal(true); };
  const handleView = (contract) => { setSelectedContract(contract); setModalMode('view'); setShowModal(true); };
  const handleEdit = (contract) => { setSelectedContract(contract); setModalMode('edit'); setShowModal(true); };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '50' }));
    setSearchFields(ALL_SEARCH_KEYS);
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;

  const handleArchive = async (contract) => {
    const archiving = !contract.is_archived;
    try {
      const response = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_archived: archiving })
      });
      if (response.ok) loadContracts();
      else setAlertDialog({ isOpen: true, message: `Failed to ${archiving ? 'archive' : 'unarchive'} pricing entry`, type: 'error' });
    } catch (error) {
      console.error('Error archiving pricing:', error);
      setAlertDialog({ isOpen: true, message: `Error ${archiving ? 'archiving' : 'unarchiving'} pricing entry`, type: 'error' });
    }
  };

  const refreshRows = async ({ contractId } = {}) => {
    if (!contractId) return loadContracts();
    try {
      const params = new URLSearchParams({ id: contractId });
      const res = await fetch(`${API_URL}/api/pricing/contracts?${params}`, { credentials: 'include' });
      const data = await res.json();
      const refreshed = data.data || [];
      setContracts(prev => {
        const byId = new Map(refreshed.map(c => [c.id, c]));
        const updated = prev.map(c => byId.has(c.id) ? byId.get(c.id) : c);
        const existingIds = new Set(prev.map(c => c.id));
        const newRows = refreshed.filter(c => !existingIds.has(c.id));
        return [...updated, ...newRows];
      });
    } catch (err) {
      console.error('Error refreshing rows:', err);
      await loadContracts();
    }
  };

  const handleSave = async (savedInfo = {}) => {
    setShowModal(false);
    await loadPeriods();
    await refreshRows(savedInfo);
  };

  const handleToggleHold = async (contract) => {
    try {
      await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/hold`, { method: 'PATCH', credentials: 'include' });
      loadContracts();
    } catch (error) {
      console.error('Error toggling hold:', error);
    }
  };

  const handleBulkHold = async (onHold) => {
    if (selectedIds.length === 0) return;
    try {
      await fetch(`${API_URL}/api/pricing/contracts/bulk-hold`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds, on_hold: onHold })
      });
      setSelectedIds([]);
      loadContracts();
    } catch (error) {
      console.error('Error bulk toggling hold:', error);
    }
  };

  const handleRefreshCost = async (contract) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/contracts/${contract.id}/refresh-cost`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (data.skipped) {
        setAlertDialog({ isOpen: true, message: data.reason || 'No purchase_unit_price on inventory item', type: 'warning' });
        return;
      }
      await loadContracts();
      setAlertDialog({ isOpen: true, message: `Cost refreshed to ${formatPrice(data.newCost ?? 0)}`, type: 'success' });
    } catch (error) {
      console.error('Error refreshing cost:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to refresh cost', type: 'error' });
    }
  };

  const handleBulkRefreshCost = () => {
    if (selectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      message: `Refresh cost for ${selectedIds.length} contract${selectedIds.length !== 1 ? 's' : ''} from the current inventory cost? Margins will recalculate against the existing prices.`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const res = await fetch(`${API_URL}/api/pricing/contracts/refresh-costs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids: selectedIds })
          });
          const data = await res.json();
          setSelectedIds([]);
          await loadContracts();
          const updated = data.updated?.length || 0;
          const skipped = data.skipped?.length || 0;
          const failed = data.failed?.length || 0;
          const parts = [`${updated} refreshed`];
          if (skipped > 0) parts.push(`${skipped} skipped (no inventory cost)`);
          if (failed > 0) parts.push(`${failed} failed`);
          setAlertDialog({ isOpen: true, message: parts.join(', '), type: failed > 0 ? 'warning' : 'success' });
        } catch (error) {
          console.error('Error bulk refreshing costs:', error);
          setAlertDialog({ isOpen: true, message: 'Failed to bulk refresh costs', type: 'error' });
        }
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedContracts.length) setSelectedIds([]);
    else setSelectedIds(paginatedContracts.map(c => c.id));
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedIds(prev => {
        if (checked) return prev.includes(id) ? prev : [...prev, id];
        return prev.filter(i => i !== id);
      });
    }
  });

  // The context-menu hook works in Sets; this page keeps selection as an array.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const ctxMenu = useTableContextMenu({
    rows: paginatedContracts,
    selectedIds: selectedIdSet,
    getRowId: (c) => c.id
  });

  const allPageSelected = paginatedContracts.length > 0 && selectedIds.length === paginatedContracts.length;

  // Shared by the row's kebab menu and its right-click context menu.
  /**
   * Print every selected row as its own contract, in one PDF.
   *
   * Selection is by contract row id but the printable unit is the GROUP, so
   * several members of one group selected together produce one contract rather
   * than repeated copies of it. Order follows the table, so the printed stack
   * matches the screen.
   */
  const handleBulkPrintContracts = () => {
    const selected = new Set(selectedIds);
    const seen = new Set();
    const rows = [];
    for (const c of filteredAndSortedContracts) {
      if (!selected.has(c.id)) continue;
      const key = c.contract_group_id ? `g:${c.contract_group_id}` : `c:${c.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(c.contract_group_id ? { groupId: c.contract_group_id } : { contractId: c.id });
    }
    if (!rows.length) return;
    openContractPdf(rows, {
      onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
    });
  };

  // Mark / unmark fulfilled from the row menus. A grouped contract changes as a
  // whole on the server.
  // Make a draft contract live — its whole group, after the server has checked
  // every row's own dates against live contracts (Cam, 2026-10-22).
  const handleMakeLive = async (contract) => {
    try {
      const data = await makeContractLive(contract.id);
      await loadContracts();
      setAlertDialog({
        isOpen: true,
        type: 'success',
        message: data.made_live > 1 ? `${data.made_live} contract rows are now live.` : 'The contract is now live.'
      });
    } catch (e) {
      setAlertDialog({ isOpen: true, message: e.message, type: 'warning' });
    }
  };

  const handleSetFulfilled = async (ids, isFulfilled) => {
    try {
      await setContractsFulfilled(ids, isFulfilled);
      loadContracts();
    } catch (e) {
      setAlertDialog({ isOpen: true, message: `Failed to ${isFulfilled ? 'mark' : 'unmark'} the contract fulfilled: ${e.message}`, type: 'error' });
    }
  };

  const buildRowActions = (contract) => [
    {
      key: 'contract',
      icon: 'description',
      label: 'Print contract',
      title: 'Two-page sales contract with terms overleaf — print double-sided',
      onClick: () => openContractPdf([contract.contract_group_id ? { groupId: contract.contract_group_id } : { contractId: contract.id }], {
        onError: (message, type) => setAlertDialog({ isOpen: true, message, type: type || 'error' })
      })
    },
    { key: 'view', icon: 'visibility', label: 'View', onClick: () => handleView(contract) },
    { key: 'orders', icon: 'playlist_add', label: 'Add orders…', title: 'The orders counted against this contract — add any of this customer\'s orders by hand, or take one off', onClick: () => setOrdersFor({ set: { kind: 'contract', ids: [contract.id] }, title: `${contract.company_name} — ${contract.product_name || 'contract'}` }) },
    { key: 'notes', icon: 'sticky_note_2', label: contract.notes ? 'View Notes' : 'No Notes', disabled: !contract.notes, onClick: () => setNotesDialog({ isOpen: true, notes: contract.notes, title: `${contract.company_name} - ${contract.product_name}` }) },
    { key: 'hold', icon: contract.is_on_hold ? 'play_circle' : 'pause_circle', label: contract.is_on_hold ? 'Resume' : 'Hold', className: contract.is_on_hold ? 'action-unhold' : 'action-hold', onClick: () => handleToggleHold(contract) },
    fulfilledAction({ fulfilled: !!contract.is_fulfilled_override, onToggle: () => handleSetFulfilled([contract.id], !contract.is_fulfilled_override) }),
    { key: 'edit', icon: 'edit', label: 'Edit', onClick: () => handleEdit(contract) },
    makeLiveAction({ draft: !!contract.is_draft, onClick: () => handleMakeLive(contract) }),
    { key: 'refresh', icon: 'sync', label: 'Refresh cost', onClick: () => handleRefreshCost(contract) },
    { key: 'archive', icon: contract.is_archived ? 'unarchive' : 'archive', label: contract.is_archived ? 'Unarchive' : 'Archive', onClick: () => handleArchive(contract) },
  ];

  const headerBlurb = infoText || `Manage ${title} contract pricing`;

  return (
    <div className={`contract-pricing ${keyPrefix}`}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">{headerBlurb}</span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <RecalculateQuantitiesButton user={user} onDone={() => loadContracts()} onAlert={setAlertDialog} />
            <RefreshAutoLinksButton user={user} periods={periods} onDone={() => loadContracts()} onAlert={setAlertDialog} />
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: 'var(--spacing-md' }}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr 150px 135px 195px 150px 60px 85px 70px' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['customer', 'account no.', 'product', 'product code', 'contract no. (ours and the supplier\'s)', 'or the fields chosen in Search In']} />
            </label>
            <Input
              type="text"
              placeholder='Terms, or "exact phrase"…'
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Search In</label>
            <MultiSelectDropdown
              options={SEARCH_FIELD_OPTIONS}
              value={searchFields}
              onChange={setSearchFields}
              summarize={searchFieldsSummary}
              hideTags
              placeholder="Search All"
            />
          </div>

          <div className="filter-group">
            <label>Contract Period</label>
            <Dropdown
              value={filters.period}
              onChange={(value) => handleFilterChange('period', value)}
              options={[
                { value: '', label: 'All Periods' },
                ...periods.map(p => ({ value: p.id.toString(), label: p.name }))
              ]}
              placeholder="All Periods"
            />
          </div>

          <div className="filter-group">
            <label>Supplier</label>
            <Dropdown
              value={filters.mill}
              onChange={(value) => handleFilterChange('mill', value)}
              options={[
                { value: '', label: 'All Suppliers' },
                ...mills.map(supplier => ({ value: supplier.id.toString(), label: supplier.company_name }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Rep</label>
            <Dropdown
              value={filters.rep}
              onChange={(value) => handleFilterChange('rep', value)}
              options={[
                { value: '', label: 'All Reps' },
                ...reps.map(rep => ({ value: rep, label: rep }))
              ]}
            />
          </div>

          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id={`show-archived-${keyPrefix}`}
                  type="checkbox"
                  checked={!!filters.showArchived}
                  onChange={(e) => handleFilterChange('showArchived', e.target.checked)}
                />
                <label htmlFor={`show-archived-${keyPrefix}`}></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label title="Untick to hide rows that already have a contract number attached (ours or supplier's).">Contracted</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id={`show-contracted-${keyPrefix}`}
                  type="checkbox"
                  checked={!!filters.showContracted}
                  onChange={(e) => handleFilterChange('showContracted', e.target.checked)}
                />
                <label htmlFor={`show-contracted-${keyPrefix}`}></label>
              </div>
            </div>
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="50"
            />
          </div>
        </div>
      </div>

      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          <ResetButton />
        </div>
        {pagination.totalPages > 0 && (
          <PaginationControls pagination={pagination} onPageChange={changePage} />
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id={`bulk-${keyPrefix}-indicator`} checked readOnly />
              <label htmlFor={`bulk-${keyPrefix}-indicator`} className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button className="btn-slideout" onClick={handleBulkPrintContracts}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">description</span></div>
                <span className="btn-label">Print Contracts</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout btn-hold" onClick={() => handleBulkHold(true)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">pause_circle</span></div>
                <span className="btn-label">Hold</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout btn-unhold" onClick={() => handleBulkHold(false)}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">play_circle</span></div>
                <span className="btn-label">Resume</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleBulkRefreshCost}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">sync</span></div>
                <span className="btn-label">Refresh Costs</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout btn-danger" onClick={() => setSelectedIds([])}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Clear</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input type="checkbox" className="tick-check-input" id={`select-all-${keyPrefix}`} checked={paginatedContracts.length > 0 && selectedIds.length === paginatedContracts.length} onChange={toggleSelectAll} />
                    <label htmlFor={`select-all-${keyPrefix}`} className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="sortable" onClick={() => handleSort('company_name')}>
                  Customer {sorting.field === 'company_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={1} />
                </th>
                <th className="sortable" onClick={() => handleSort('product_name')}>
                  Product {sorting.field === 'product_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th className="sortable" onClick={() => handleSort('contract_number')}>
                  Contract {sorting.field === 'contract_number' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th style={{ textAlign: 'center' }} className="sortable" onClick={() => handleSort('period_names')}>
                  Period(s) {sorting.field === 'period_names' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th className="sortable" onClick={() => handleSort('start_date')}>
                  Dates {sorting.field === 'start_date' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th className="sortable" onClick={() => handleSort('mill')}>
                  Supplier {sorting.field === 'mill' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('quantity')}>
                  Quant {sorting.field === 'quantity' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th className="text-right sortable" onClick={() => handleSort('margin')}>
                  Price / Margin {sorting.field === 'margin' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th className="sortable" onClick={() => handleSort('rep_name')}>
                  Rep {sorting.field === 'rep_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={9} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={10} /></th>
              </tr>
            </thead>
            <tbody>
              {paginatedContracts.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center">No contracts found</td>
                </tr>
              ) : (
                paginatedContracts.map((contract) => (
                  <tr key={contract.id} className={contract.is_archived ? 'archived' : ''} style={{ cursor: 'pointer', ...(contract.is_on_hold ? { opacity: 0.6 } : {}) }} onClick={() => handleEdit(contract)} onContextMenu={ctxMenu.onRowContextMenu(contract)}>
                    <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <label
                        className="tick-check-label"
                        style={{ justifyContent: 'center', gap: '0' }}
                        onMouseDown={dragSelect.onMouseDown(contract.id, selectedIds.includes(contract.id))}
                        onMouseEnter={dragSelect.onMouseEnter(contract.id)}
                        onClick={(e) => e.preventDefault()}
                      >
                        <input type="checkbox" className="tick-check-input" id={`select-${keyPrefix}-${contract.id}`} checked={selectedIds.includes(contract.id)} onChange={() => toggleSelect(contract.id)} />
                        <label htmlFor={`select-${keyPrefix}-${contract.id}`} className="tick-check">
                          <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                        </label>
                      </label>
                    </td>
                    <td>
                      <ContractCustomerCell
                        accountNo={contract.account_no}
                        accountId={contract.account_id}
                        onAccountClick={() => setSelectedContactId(contract.account_id)}
                        name={contract.company_name}
                        onNameClick={() => { if (contract.account_id) setSelectedContactId(contract.account_id); }}
                        flags={{ hold: contract.is_on_hold, draft: contract.is_draft, archived: contract.is_archived }}
                        status={(() => {
                          const c = parseFloat(contract.quantity || 0);
                          const u = parseFloat(contract.ordered_quantity || 0);
                          return !contract.is_fulfilled_override && c > 0 && (c - u) < 1 ? 'FULFILLED' : null;
                        })()}
                        manualFulfilled={contract.is_fulfilled_override}
                        linkedPoCount={contract.linked_po_count}
                        onLinkedClick={() => handleView(contract)}
                        address={contract.address_line}
                        scopeKind={contract.scope_kind}
                      />
                    </td>
                    <td>
                      <ProductCell
                        productCode={contract.product_code}
                        additiveCode={contract.additive_code}
                        productName={contract.product_name}
                        isHolding={contract.is_holding_product}
                        additiveName={contract.additive_name}
                      />
                      <ContractGroupBadge group={contract.product_group} />
                    </td>
                    <td>{contract.contract_number
                      ? <span className="our-contract-chip" title="Our contract number (Excel Agri ↔ customer)">{contract.contract_number}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ textAlign: 'center' }}>
                      {(contract.period_names || contract.period_name || '').split(', ').filter(Boolean).map((p, i) => (
                        <React.Fragment key={i}>
                          <span className={`period-badge ${p.toLowerCase().startsWith('winter') ? 'winter' : 'summer'}${isPastPeriodName(periods, p) ? ' past-period' : ''}`}>{p}</span>
                          {isCurrentPeriodName(p) && contractCoversToday(contract) && (
                            <span className="material-symbols-rounded period-current-icon" title="Current — this contract’s dates cover today">check_circle</span>
                          )}
                        </React.Fragment>
                      ))}
                    </td>
                    <td>
                      <div>{contract.start_date ? new Date(contract.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                      <div>{contract.end_date ? new Date(contract.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-'}</div>
                    </td>
                    <td>{contract.supplier_name || contract.mill || '-'}</td>
                    <td className="text-right">{contract.quantity ? formatQuantity(contract.quantity) : '-'}</td>
                    <td className="text-right">
                      <PriceMarginCell price={contract.price} cost={contract.cost} margin={contract.margin} />
                    </td>
                    <td>{contract.rep_name || '-'}</td>
                    <td className="actions-col" style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu actions={buildRowActions(contract)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {ordersFor && (
        <ContractOrdersModal
          set={ordersFor.set}
          title={ordersFor.title}
          onClose={() => { setOrdersFor(null); loadContracts(); }}
        />
      )}

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          {
            key: 'bulk-hold',
            icon: 'pause_circle',
            label: `Hold ${ids.size} contracts`,
            onClick: () => handleBulkHold(true)
          },
          {
            key: 'bulk-resume',
            icon: 'play_circle',
            label: `Resume ${ids.size} contracts`,
            onClick: () => handleBulkHold(false)
          },
          {
            key: 'bulk-refresh',
            icon: 'sync',
            label: `Refresh cost on ${ids.size} contracts`,
            onClick: handleBulkRefreshCost
          }
        ]}
        actions={[
          { key: 'reload', icon: 'refresh', label: 'Reload contracts', onClick: loadContracts },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters },
          {
            key: 'select-all',
            icon: allPageSelected ? 'deselect' : 'select_all',
            label: allPageSelected ? 'Clear selection' : 'Select all on this page',
            disabled: paginatedContracts.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'show-archived',
            icon: 'archive',
            label: 'Show archived',
            active: !!filters.showArchived,
            onClick: () => handleFilterChange('showArchived', !filters.showArchived)
          },
          {
            key: 'show-contracted',
            icon: 'description',
            label: 'Show contracted',
            active: !!filters.showContracted,
            onClick: () => handleFilterChange('showContracted', !filters.showContracted)
          }
        ]}
      />

      {showModal && (
        <ContractPricingModal
          contract={selectedContract}
          categoryId={categoryId}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadContracts(); }}
          onSave={handleSave}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <AlertDialog
        isOpen={notesDialog.isOpen}
        title={notesDialog.title}
        message={notesDialog.notes}
        type="info"
        onClose={() => setNotesDialog({ isOpen: false, notes: '', title: '' })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal contactId={selectedContactId} onClose={() => setSelectedContactId(null)} />,
        document.body
      )}
    </div>
  );
}

export default GenericContractPricing;
